import { DocumentationRequiredType, DocumentStatus, patchSetDocumentStatus } from '@nemuru-eng/js-api-client';
import { useEffect, useState } from 'react';
import { FormProvider } from 'react-hook-form';

import { ConfirmationCheckbox } from '@/components/modules/DocumentsValidation/components/_shared/ConfirmationCheckbox';
import { Section } from '@/components/modules/DocumentsValidation/components/_shared/Section';
import { ValidationButtons } from '@/components/modules/DocumentsValidation/components/_shared/ValidationButtons';
import { IncomeDeclaredDataFactory } from '@/components/modules/DocumentsValidation/components/steps/IncomeValidation/components/IncomeDeclaredDataFactory';
import { useDocumentsValidationContext } from '@/components/modules/DocumentsValidation/contexts/DocumentsValidationContext';
import { useFetchValidationData } from '@/components/modules/DocumentsValidation/hooks/useFetchValidationData';
import { DocumentationRequiredMap } from '@/components/modules/DocumentsValidation/maps/documentationRequired.map';
import { ValidationSchema } from '@/components/modules/DocumentsValidation/schemas/validation.schema';
import { ValidationStep } from '@/components/modules/DocumentsValidation/types/ValidationStep';
import { updateBorrowerDocumentationRequired } from '@/components/modules/DocumentsValidation/utils/updateBorrowerDocumentationRequired';
import { CustomSkeleton } from '@/components/shared/components/CustomSkeleton';
import { DocumentVisualizer } from '@/components/shared/components/DocumentVisualizer';
import { useErrorBoundary } from '@/components/shared/components/ErrorBoundary/hooks/useErrorBoundary';
import { FlexColumn } from '@/components/shared/components/FlexColumn';
import { Font } from '@/components/shared/components/Font';
import { FieldVariant } from '@/components/shared/components/NewContextForms/types/FieldVariant';
import { Text } from '@/components/shared/components/Text';
import { useCustomForm } from '@/components/shared/hooks/useCustomForm';

interface IncomeValidationProps {
  onNext: () => void;
}

export const IncomeValidation = ({ onNext }: IncomeValidationProps) => {
  const { loan, borrowerOnboardingDetails, borrowerDocumentationRequired, update } = useDocumentsValidationContext();
  const { throwErrorInAlert } = useErrorBoundary();
  const { incomeFiles, income, fetchIncomeValidationData, loading } = useFetchValidationData();
  const form = useCustomForm(ValidationSchema, { confirmation: false });

  const [lazyElementLoading, setLazyElementLoading] = useState(true);
  const [loadingValidation, setLoadingValidation] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      fetchIncomeValidationData(loan, borrowerOnboardingDetails, borrowerDocumentationRequired);
    } catch (error) {
      console.error(error);
      throwErrorInAlert('INCOME_VALIDATION_ERROR', () => fetchData());
    }
  };

  const onSubmit = async (validate: boolean) => {
    const files = DocumentationRequiredMap.getFilesByStep(
      borrowerDocumentationRequired,
      ValidationStep.INCOME_VALIDATION,
    );

    if (!files) {
      try {
        onNext();
      } catch (error) {}
      return;
    }

    const documentationRequiredId = borrowerDocumentationRequired.id;
    const unvalidatedFiles = files.filter((file) => file.status !== DocumentStatus.VALIDATED);

    try {
      setLoadingValidation(true);
      await Promise.all(
        unvalidatedFiles.map((file) => {
          return patchSetDocumentStatus({
            id: documentationRequiredId,
            fileTypeId: file.id,
            documentationRequiredType: DocumentationRequiredType.BORROWER,
            documentStatus: validate ? DocumentStatus.VALIDATED : DocumentStatus.INCIDENCE,
          });
        }),
      );
      updateBorrowerDocumentationRequired({
        borrowerDocumentationRequired,
        filesToUpdate: unvalidatedFiles,
        validate,
        update,
      });
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingValidation(false);
    }

    onNext();
  };

  return (
    <Section.Content>
      <Section.Header title={`DOCUMENTS_VALIDATION_STEP_${ValidationStep.INCOME_VALIDATION}`} />
      <FlexColumn gridGap={8} width="100%">
        <Font variant="subtitle2" color="textSecondary">
          <Text tId={'DECLARED_DATA'} />
        </Font>
        {income ? (
          <IncomeDeclaredDataFactory borrowerOnboardingDetails={borrowerOnboardingDetails} income={income} />
        ) : (
          <CustomSkeleton height={'196px'} />
        )}
      </FlexColumn>
      <FlexColumn gridGap={8} width="100%" mt={2}>
        <Font variant="subtitle2" color="textSecondary">
          <Text tId={'DOCUMENT'} />
        </Font>
        {incomeFiles?.map((incomeFile, i) => (
          <DocumentVisualizer
            key={`${incomeFile.type}-${i}`}
            id={`visualizer-income-${i}`}
            documentFile={incomeFile}
            loading={loading}
            containerStyle={{ maxHeight: '400px' }}
            isFullWidth
            onLoad={() => {
              setLazyElementLoading(false);
            }}
          />
        ))}
      </FlexColumn>
      <FormProvider {...form}>
        <ConfirmationCheckbox label={'INCOME_VALIDATION_CHECKBOX'} fieldVariant={FieldVariant.form} />
      </FormProvider>
      <Section.Footer>
        <ValidationButtons
          onValidate={form.handleSubmit(() => onSubmit(true))}
          onDoNotValidate={() => onSubmit(false)}
          disabled={loading || lazyElementLoading || loadingValidation}
        />
      </Section.Footer>
    </Section.Content>
  );
};
